/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { WritableAtom, atom } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { OriginType, SanctioningBodyEnum } from 'models/viewModels';
import { useCallback } from 'react';
import { workspaceModifiedAtom } from 'jotai/workspace';
import { userPreferencesService } from 'services/userPreferences/UserPreferencesServices';
import { workspaceService } from 'services/workspace/WorkspaceService';
import { QtmThemeSetting } from 'theme';
import { UserPreferencesObject } from '.';

export const userPreferencesStringAtom = atom<string>('');
export const userPreferencesAtom = atom<UserPreferencesObject>({} as UserPreferencesObject);
export const colorModeAtom = atom<QtmThemeSetting | Promise<QtmThemeSetting>, [QtmThemeSetting], void>(
  (get) => {
    const currentUserPreferences = get(userPreferencesAtom);
    // need to call userPreferencesService.getColorMode() if userPreferences is empty
    if (Object.keys(currentUserPreferences).length === 0) {
      return userPreferencesService.getColorMode();
    }
    if (Object.keys(currentUserPreferences).length > 0) {
      return currentUserPreferences.colorMode;
    } else {
      return 'light';
    }
  },
  (get, set, newValue: QtmThemeSetting) => {
    const userPreferences = get(userPreferencesAtom);
    if (newValue) {
      userPreferences.colorMode = newValue;
      set(userPreferencesAtom, { ...userPreferences });
      userPreferencesService.updateUserPreferences(userPreferences);
    }
  }
);

const initialAutoSaveWorkspaceAtom = atom<Promise<boolean>>(async (_get) => {
  const value = await userPreferencesService.getAutoSaveWorkspace();
  return value;
});
const overwrittenAutoSaveWorkspaceAtom = atom(null);
export const autoSaveWorkspaceAtom = atom(
  (get) => get(overwrittenAutoSaveWorkspaceAtom) ?? get(initialAutoSaveWorkspaceAtom),
  async (get, set, newValue) => {
    //needed to allow overwrittenAutoSaveWorkspaceAtom to be null
    const nextValue = typeof newValue === 'function' ? newValue(get(autoSaveWorkspaceAtom)) : newValue;
    await userPreferencesService.setAutoSaveWorkspace(nextValue);
    set(overwrittenAutoSaveWorkspaceAtom, nextValue);
  }
);

export const useToggleAutoSaveWorkspace = () => {
  return useAtomCallback(
    useCallback(async (get, set) => {
      const autoSaveOn = await get(autoSaveWorkspaceAtom);
      const isWorkspaceModified = get(workspaceModifiedAtom);
      set(autoSaveWorkspaceAtom, !autoSaveOn);
      if (!autoSaveOn && isWorkspaceModified) {
        await workspaceService.saveWorkspace();
      }
    }, [])
  );
};

export const sanctioningBodySelectionOpenAtom = atom<boolean>(false);

export const dataFeedMonitorToggleAtom = atom<boolean, [boolean], void>(
  (get) => {
    const userPreferences = get(userPreferencesAtom);
    if (Object.keys(userPreferences).length > 0) {
      return userPreferences.dataFeedMonitor;
    } else {
      return true;
    }
  },
  (get, set, newValue: boolean) => {
    const userPreferences = get(userPreferencesAtom);
    userPreferences.dataFeedMonitor = newValue;
    set(userPreferencesAtom, { ...userPreferences });
    userPreferencesService.setDataFeedMonitor(newValue);
  }
);

export const defaultSanctioningBodyAtom = atom<string | undefined, [string | undefined], void>(
  (get) => {
    const userPreferences = get(userPreferencesAtom);
    if (Object.keys(userPreferences).length > 0) {
      return userPreferences.defaultSanctioningBody;
    } else {
      return undefined;
    }
  },
  (get, set, newValue: string | undefined) => {
    const userPreferences = get(userPreferencesAtom);
    userPreferences.defaultSanctioningBody = newValue;
    set(userPreferencesAtom, { ...userPreferences });
    userPreferencesService.setDefaultSanctioningBody(newValue);
  }
);

export const defaultSanctioningBodyFeedAtom = atom<OriginType | undefined, [OriginType | undefined], void>(
  (get) => {
    const userPreferences = get(userPreferencesAtom);
    if (Object.keys(userPreferences).length > 0) {
      return userPreferences.defaultSanctioningBodyFeed;
    } else {
      return undefined;
    }
  },
  (get, set, newValue: OriginType | undefined) => {
    const userPreferences = get(userPreferencesAtom);
    userPreferences.defaultSanctioningBodyFeed = newValue;
    set(userPreferencesAtom, { ...userPreferences });
    userPreferencesService.setDefaultSanctioningBody(undefined, newValue);
  }
);

// export const sessionDefaultWorkspaceIdAtomFamily = atomFamily<string, SessionType>({
//   key: 'sessionDefaultWorkspaceIdAtomFamily',
//   default: (sessionType) => userPreferencesService.getSessionDefaultWorkspaceId(sessionType),
//   effects_UNSTABLE: (sessionType: SessionType) => [
//     ({ onSet, setSelf, trigger }) => {
//       const load = async () => {
//         const workspaceId = await userPreferencesService.getSessionDefaultWorkspaceId(sessionType);
//         setSelf(workspaceId);
//       };

//       if (trigger === 'get') {
//         load();
//       }

//       onSet((newValue) => {
//         userPreferencesService.updateSessionDefaultWorkspaceId(sessionType, newValue);
//       });
//     },
//   ],
// });

export const sanctioningBodyDefaultWorkspaceIdAtomFamily = atomFamily<
  SanctioningBodyEnum,
  WritableAtom<string | Promise<string>, [string], void>
>((sanctioningBodyId: SanctioningBodyEnum) =>
  atom<string | Promise<string>, [string], void>(
    (get) => {
      const userPreferences = get(userPreferencesAtom);
      if (Object.keys(userPreferences).length > 0) {
        return userPreferences.sanctioningBodyDefaultWorkspaces[sanctioningBodyId];
      } else {
        return userPreferencesService.getSanctioningBodyDefaultWorkspaceId(sanctioningBodyId);
      }
    },
    (get, set, newValue: string) => {
      const userPreferences = get(userPreferencesAtom);
      userPreferences.sanctioningBodyDefaultWorkspaces[sanctioningBodyId] = newValue;
      set(userPreferencesAtom, { ...userPreferences });
      userPreferencesService.updateSanctioningBodyDefaultWorkspaceId(sanctioningBodyId, newValue);
    }
  )
);
